import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { apiClient } from '../../services/apiClient';
import { Client } from '../../types/client';
import { parseImeis } from '../../utils/imeis';

interface AssignDevicesModalProps {
  target: Client | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called once at least one gateway moved, so the lists reload. */
  onDone?: () => void;
}

interface Outcome { imei: string; ok: boolean; detail?: string }

/**
 * Gateways assigned to an agency by pasting their IMEIs. Each one is a
 * plain client change on the device (PATCH idClient); inside a group the
 * backend regenerates the configuration without rebooting. The scope rules
 * apply: a gateway outside the caller's clients is refused and reported.
 */
export const AssignDevicesModal: React.FC<AssignDevicesModalProps> = ({ target, isOpen, onClose, onDone }) => {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const [running, setRunning] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !target) return null;

  const imeis = parseImeis(text);

  const close = () => {
    if (running) return;
    setText('');
    setOutcomes(null);
    setError(null);
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imeis.length === 0) {
      setError(t('clients.agencies.assignModal.empty'));
      return;
    }
    setError(null);
    setRunning(true);
    const results: Outcome[] = [];
    for (const imei of imeis) {
      try {
        await apiClient.getAxiosInstance().patch(`/v2/conf/${imei}`, { idClient: target.id });
        results.push({ imei, ok: true });
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        results.push({ imei, ok: false, detail: typeof detail === 'string' ? detail : err?.message });
      }
    }
    setOutcomes(results);
    setRunning(false);
    if (results.some((r) => r.ok)) onDone?.();
  };

  const moved = outcomes?.filter((r) => r.ok).length ?? 0;

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="config-template-modal tpc ptc" data-testid="modal-assign-devices" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('clients.agencies.assignModal.title', { name: target.name })}</h2>
              <div className="tpd-title-sub"><span>{t('clients.agencies.assignModal.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={close} disabled={running} aria-label={t('common.close')}>×</button>
        </div>

        <form onSubmit={submit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {error && <div className="error-message" role="alert">{error}</div>}
            <section className="cu-section">
              <div className="cu-field cu-field-wide">
                <label htmlFor="assign-imeis">{t('clients.agencies.assignModal.imeis')}</label>
                <textarea id="assign-imeis" data-testid="field-assign-imeis" className="cu-input mono" rows={6}
                  value={text} onChange={(e) => { setText(e.target.value); setOutcomes(null); }} disabled={running} autoFocus />
                <span className="cu-hint">{t('clients.agencies.assignModal.hint', { count: imeis.length })}</span>
              </div>
              {outcomes && (
                <div className="pt-resolved" data-testid="assign-results">
                  <p className={`cu-hint${moved === outcomes.length ? ' is-ok' : ''}`}>{t('clients.agencies.assignModal.done', { moved, count: outcomes.length })}</p>
                  <ul className="pt-devices">
                    {outcomes.map((r) => (
                      <li key={r.imei} className={r.ok ? '' : 'is-problem'}>
                        <code className="dt-code">{r.imei}</code>
                        <span className={`pt-device-state ${r.ok ? 'is-moved' : 'is-failed'}`} title={r.detail}>
                          {r.ok ? t('clients.agencies.assignModal.moved') : t('clients.agencies.assignModal.failed')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>
          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary"><span className="tpd-muted">{target.name}</span></div>
            <div className="tpc-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={close} disabled={running}>{t('common.close')}</button>
              <button type="submit" className="btn btn-primary" data-testid="btn-assign-submit" disabled={running || imeis.length === 0}>
                {running ? t('clients.agencies.assignModal.running') : t('clients.agencies.assignModal.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
